/* eslint-disable max-classes-per-file */
import {
  ButtonWithAnchorStyles,
  Checkbox,
  PopupConsent,
} from '@axo/deprecated/util/ui-components';
import {
  getMarketingConsentPartners,
  getMarketingConsentServices,
  getMarketingConsentText,
} from '@axo/form/configs';
import { reloadData } from '@axo/form/data-access/backend-sync';
import React, { CSSProperties, MouseEventHandler } from 'react';
import {
  ElementFactory,
  FunctionFactory,
  Question,
  Serializer,
  SurveyModel,
} from 'survey-core';
import {
  ReactQuestionFactory,
  SurveyQuestionElementBase,
} from 'survey-react-ui';
import getBasicQuestionProps from '../../utils/getBasicQuestionProps';

class QuestionModel extends Question {
  questionType: string;

  constructor(questionType: string, name: string) {
    super(name);
    this.questionType = questionType;
    this.getType = this.getType.bind(this);
  }

  getType(): string {
    const type = this.questionType ?? '';
    return type;
  }
}

type QuestionProps = {
  creator?: any;
  isDisplaymode?: boolean;
  question?: QuestionModel;
};

const CONSENT_CHANGED_AT_PAGE_NO = 'CONSENT_CHANGED_AT_PAGE_NO';
const consentChangedAtPageNoStorage = {
  get: () =>
    typeof sessionStorage !== 'undefined'
      ? parseInt(sessionStorage.getItem(CONSENT_CHANGED_AT_PAGE_NO) || '', 10)
      : undefined,
  set: (pageNo: number) =>
    sessionStorage.setItem(CONSENT_CHANGED_AT_PAGE_NO, `${pageNo}`),
  delete: () => sessionStorage.removeItem(CONSENT_CHANGED_AT_PAGE_NO),
};

export class SurveyQuestion extends SurveyQuestionElementBase {
  constructor(props: QuestionProps) {
    super(props);
    this.state = {
      errors: [],
      isPartnersModalOpen: false,
      isServicesModalOpen: false,
      shouldRetrieveConsent: true,
    };
  }

  componentDidMount(): void {
    if (window.sessionStorage === undefined) return;
    const shouldRetrieveConsent =
      reloadData.get().shouldRetrieveMarketingConsent;

    // do not update state if flag is not set, should default to true
    if (shouldRetrieveConsent === null) return;

    this.setState({
      // get value as boolean
      shouldRetrieveConsent: shouldRetrieveConsent === 'true',
    });
  }

  protected get question(): QuestionModel {
    return this.questionBase as QuestionModel;
  }

  get value() {
    return this.question.value;
  }

  // support readOnly and designMode
  get style() {
    return this.question.getPropertyValue('readOnly') ||
      this.question.isDesignMode
      ? ({
          pointerEvents: 'none',
        } as CSSProperties)
      : undefined;
  }

  protected renderElement(): JSX.Element {
    const { style, label, ...props } = getBasicQuestionProps(
      this,
      this.questionBase
    );

    const PartnersModal = () => (
      <PopupConsent
        title="Kumppanit"
        type="partners"
        buttonText="OK"
        onClick={togglePartnersModal}
        onBackgroundClick={togglePartnersModal}
        partnersList={getMarketingConsentPartners('fi')}
      />
    );

    const ServicesModal = () => (
      <PopupConsent
        title="Palvelut"
        type="services"
        buttonText="OK"
        onClick={toggleServicesModal}
        onBackgroundClick={toggleServicesModal}
        partnersList={getMarketingConsentServices('fi')}
      />
    );

    const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      consentChangedAtPageNoStorage.set(
        (this.question.survey as SurveyModel).currentPageNo
      );
      this.question.value = event.target.checked;
    };

    const togglePartnersModal = () => {
      this.setState({ isPartnersModalOpen: !this.state.isPartnersModalOpen });
    };

    const toggleServicesModal = () => {
      this.setState({ isServicesModalOpen: !this.state.isServicesModalOpen });
    };

    const getShouldShowConsent = () => {
      // default to true when sessionstorage is not available
      if (typeof window === 'undefined') return true;
      const pageNo = (this.question.survey as SurveyModel).currentPageNo;
      return (
        (pageNo === 0 && this.state.shouldRetrieveConsent) ||
        (pageNo === 4 && this.state.shouldRetrieveConsent)
      );
    };

    return (
      <>
        {getShouldShowConsent() && (
          <div style={style}>
            <Checkbox {...props} onChange={onChange}>
              <p>
                Kyllä, haluan saada Axo Finance Oy:lta ja{' '}
                <ButtonWithAnchorStyles
                  onClick={withPreventDefault(togglePartnersModal)}
                >
                  kumppaneiltamme
                </ButtonWithAnchorStyles>{' '}
                rahoitustuotteita ja{' '}
                <ButtonWithAnchorStyles
                  onClick={withPreventDefault(toggleServicesModal)}
                >
                  muita palveluita
                </ButtonWithAnchorStyles>{' '}
                koskevia tarjouksia ja tietoja kampanjoista sähköpostitse,
                tekstiviestillä ja puhelimitse. Voin koska tahansa peruuttaa
                suostumukseni ottamalla yhteyttä Axo Finance Oy:hyn.
              </p>
            </Checkbox>

            {this.state.isPartnersModalOpen && <PartnersModal />}
            {this.state.isServicesModalOpen && <ServicesModal />}
          </div>
        )}
      </>
    );
  }
}

const withPreventDefault = (fn: () => void) => {
  const wrapped: MouseEventHandler = (e) => {
    e.preventDefault();
    fn();
  };
  return wrapped;
};

export function registerMarketingConsentFinlandQuestion() {
  const questionType = 'Axo Marketing Consent Finland';

  const getMarketingConsentPartnersFI = () => getMarketingConsentPartners('fi');
  const getMarketingConsentServicesFI = () => getMarketingConsentServices('fi');
  const getMarketingConsentTextFI = () => getMarketingConsentText('fi');

  // this serializes the class into JSON
  Serializer.addClass(
    questionType,

    // Add properties that should be accessed in Creator.
    [],
    () => new QuestionModel(questionType, ''),
    'checkbox'
  );

  ReactQuestionFactory.Instance.registerQuestion(questionType, (props: any) =>
    React.createElement(SurveyQuestion, props)
  );

  ElementFactory.Instance.registerElement(
    questionType,
    (name: string) => new QuestionModel(questionType, name)
  );

  FunctionFactory.Instance.register(
    'getMarketingConsentPartnersFI',
    getMarketingConsentPartnersFI
  );
  FunctionFactory.Instance.register(
    'getMarketingConsentServicesFI',
    getMarketingConsentServicesFI
  );
  FunctionFactory.Instance.register(
    'getMarketingConsentTextFI',
    getMarketingConsentTextFI
  );
}
